import { Route } from '@angular/router';
import { openChatGuard } from 'app/layout/common/quick-chat/open-chat.guard';

import { initialDataResolver } from '#root/app.resolvers';
import { AuthGuard } from '#root/core/auth/guards/auth.guard';
import { NoAuthGuard } from '#root/core/auth/guards/no-auth.guard';

import { LayoutComponent } from './layout/layout.component';
import {
  DashboardByRoleResolver,
} from './modules/admin/dashboard/dashboard.resolver';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "dashboard" },

  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "dashboard" },

  // Public routes (no guards): employee verification page opened from the ID card QR.
  // Must stay BEFORE the guest/admin blocks: the admin block's '**' would catch it otherwise.
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "verificar/:token",
        loadComponent: () =>
          import("#root/modules/public/employee-verification/employee-verification.component")
            .then((m) => m.EmployeeVerificationComponent),
      },
    ],
  },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "forgot-password",
        loadChildren: () =>
          import(
            "#root/modules/auth/forgot-password/forgot-password.routing"
          ),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("#root/modules/auth/reset-password/reset-password.routing"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("#root/modules/auth/sign-in/sign-in.routing"),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () =>
          import("#root/modules/auth/sign-out/sign-out.routing"),
      },
    ],
  },

  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "dashboard",
        loadChildren: () =>
          import("#root/modules/admin/dashboard/dashboard.routes"),
        canActivate: [DashboardByRoleResolver],
      },
      // Enlace de las notificaciones de chat: abre el panel lateral en esa conversación
      { path: "chat/:id", canActivate: [openChatGuard], children: [] },
      {
        path: "accounting",
        children: [
          {
            path:'banks', loadChildren: () => import("#root/modules/admin/accounting/bank/bank.routes")
          },
          {
            path:'expense-accounts', loadChildren: () => import("#root/modules/admin/accounting/expense-account/expense-account.routes")
          },
          {
            path: "income-accounts", loadChildren: () => import("#root/modules/admin/accounting/income-account/income-account.routes")
          },
          {
            path: "invoices", loadChildren: () => import("#root/modules/admin/accounting/invoice-list/invoice-list.routes")
          },
          {
            path: "major-expense-accounts", loadChildren: () => import("#root/modules/admin/accounting/major-expense-account/major-expense.routes")
          },
          {
            path: "major-income-accounts", loadChildren: () => import("#root/modules/admin/accounting/major-income-account/major-income.routes")
          },
          {
            path: "purchase-bills", loadChildren: () => import("#root/modules/admin/accounting/purchase-bill-list/purchase-bill-list.routes")
          },
          {
            path: "purchase-orders", loadChildren: () => import("#root/modules/admin/accounting/purchase-list/purchase-list.routes")
          },
          {
            path:"quotations", loadChildren: () => import("#root/modules/admin/accounting/quotation-list/quotation-list.routes")
          },
          {
            path:"non-billable-expenses", loadChildren: () => import("#root/modules/admin/accounting/non-billable-expense/non-billable-expense-list.routes")
          },
          {
            path:"credit-card-payments", loadChildren: () => import("#root/modules/admin/accounting/credit-card-payment/credit-card-payment-list.routes")
          },
          {
            path:"daily-closes", loadChildren: () => import("#root/modules/admin/accounting/daily-close/daily-close.routes")
          },
          {
            path:"accounts-receivables", loadChildren: () => import("#root/modules/admin/accounting/accounts-receivable/accounts-receivable-list.routes")
          },
          {
            path:"monthly-purchase-declaration", loadChildren: () => import("#root/modules/admin/accounting/monthly-purchase-declaration/monthly-purchase-declaration-list.routes")
          },
          {
            path:"monthly-sale-declaration", loadChildren: () => import("#root/modules/admin/accounting/monthly-sale-declaration/monthly-sale-declaration-list.routes")
          },
          {
            path:"pos", loadChildren: () => import("#root/modules/admin/accounting/pos/pos.routes")
          },
          {
            path:'banks/internal-bank-accounts', loadChildren: () => import("#root/modules/admin/accounting/internal-bank-account/internal-bank-account.routes")
          },
          {
            path:"accounts-payables", loadChildren: () => import("#root/modules/admin/accounting/accounts-payable/accounts-payable-list.routes")
          },
          {
            path:"recurring-invoices", loadChildren: () => import("#root/modules/admin/accounting/recurring-invoice-list/recurring-invoice-list.routes")
          },
          {
            path: "chart-of-accounts", loadChildren: () => import("#root/modules/admin/accounting/chart-of-accounts/chart-of-accounts.routes")
          },
          {
            path: "journal-entries", loadChildren: () => import("#root/modules/admin/accounting/journal-entry/journal-entry-list.routes")
          },
          {
            path: "fiscal-periods", loadChildren: () => import("#root/modules/admin/accounting/fiscal-period/fiscal-period.routes")
          },
          {
            path: "accounting-reports", loadChildren: () => import("#root/modules/admin/accounting/accounting-reports/accounting-reports.routes")
          },
          {
            path: "accounting-config", loadChildren: () => import("#root/modules/admin/accounting/accounting-config/accounting-config.routes")
          },
        ],
      },
      {
        path: "projects",
        loadChildren: () =>
          import("#root/modules/admin/project/project-list.routes"),
      },
      {
        path: "human-resources",
        loadChildren: () =>
          import("#root/modules/admin/human-resources/human-resources.routes"),
      },
      {
        path: "crm",
        loadChildren: () =>
          import("#root/modules/admin/crm/crm.routes"),
      },
      {
        path: "ecommerce",
        loadChildren: () =>
          import("#root/modules/admin/ecommerce/ecommerce.routes"),
      },
      {
        path: "inventory",
        loadChildren: () =>
          import("#root/modules/admin/inventory/inventory.routes"),
      },
      {
        path: "globalConfig",
        // loadChildren: () =>
        //   import("#root/modules/admin/global-config/global-config.module").then(
        //     (m) => m.GlobalConfigModule
        //   ),
        children: [
          {
            path: "users",
            loadChildren: () => import("#root/modules/admin/global-config/users/users.routes")
          },
          {
            path: "lossReasons",
            loadChildren: () => import("#root/modules/admin/global-config/loss-reason/loss-reason.routes")
          },
          {
            path: "winReasons",
            loadChildren: () => import("#root/modules/admin/global-config/win-reason/win-reason.routes")
          },
          {
            path: "locations/regions",
            loadChildren: () => import("#root/modules/admin/global-config/regions/regions.routes")
          },
          {
            path: "inventory/inventory-input-type",
            loadChildren: () => import("#root/modules/admin/global-config/inventory-input-type/inventory-input-type.routes")
          },
          {
            path: "type-payment-method",
            loadChildren: () => import("#root/modules/admin/global-config/type-payment-method/type-payment-method.routes")
          },
          {
            path: "measureUnit",
            loadChildren: () => import("#root/modules/admin/global-config/unit-of-measurement/unit-of-measurement.routes")
          },
          {
            path: "status/list",
            loadChildren: () => import("#root/modules/admin/global-config/status/status.routes")
          },
          {
            path: "status/typeStatus",
            loadChildren: () => import("#root/modules/admin/global-config/type-status/type-status.routes")
          },
          {
            path: "origin/typeOrigins",
            loadChildren: () => import("#root/modules/admin/global-config/type-origin/type-origin.routes")
          },
          {
            path: "documentType",
            loadChildren: () => import("#root/modules/admin/global-config/document-type/document-type.routes")
          },
          {
            path: "internalDocument",
            loadChildren: () => import("#root/modules/admin/global-config/internal-document-type/internal-document.routes")
          },
          {
            path: "prefix",
            loadChildren: () => import("#root/modules/admin/global-config/prefix/prefix.routes")
          },
          {
            path: "tax",
            loadChildren: () => import("#root/modules/admin/global-config/taxes/tax.routes")
          },
          {
            path: "typeActivity",
            loadChildren: () => import("#root/modules/admin/global-config/type-activity/type-activity.routes")
          },
          {
            path: "locations/cities",
            loadChildren: () => import("#root/modules/admin/global-config/cities/city.routes")
          },
          {
            path: "locations/departments",
            loadChildren: () => import("#root/modules/admin/global-config/departments/department.routes")
          },
          {
            path: "discount",
            loadChildren: () => import("#root/modules/admin/global-config/discounts/discount.routes")
          },
          {
            path: "projectAttachmentCategory",
            loadChildren: () => import("#root/modules/admin/global-config/project-attachment-category/project-attachment-category.routes")
          },
          {
            path: "price-lists",
            loadChildren: () => import("#root/modules/admin/global-config/price-list/price-list.routes")
          },
          {
            path: "customer-types",
            loadChildren: () => import("#root/modules/admin/global-config/customer-type/customer-type.routes")
          }
        ]
      },
      {
        path: "settings",
        loadChildren: () =>
          import("#root/modules/admin/settings/settings.routes"),
      },
      {
        path: "error/403",
        loadChildren: () =>
          import(
            "#root/modules/miscellaneous/error/error-403/error-403.routing"
          ),
      },
      {
        path: "error/404",
        loadChildren: () =>
          import(
            "#root/modules/miscellaneous/error/error-404/error-404.routing"
          ),
      },
      {
        path: "error/500",
        loadChildren: () =>
          import(
            "#root/modules/miscellaneous/error/error-500/error-500.routing"
          ),
      },
      {
        path: "**",
        loadChildren: () =>
          import(
            "#root/modules/miscellaneous/error/error-404/error-404.routing"
          ),
      },
    ],
  },
];
